import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, Users } from "lucide-react";
import { InternalOwnership } from '@/types/journey';
import { useIsMobile } from '@/hooks/use-mobile';

interface InternalOwnershipFieldProps {
  label: string;
  ownership: InternalOwnership[];
  onChange: (ownership: InternalOwnership[]) => void;
  placeholder?: string;
}

const InternalOwnershipField: React.FC<InternalOwnershipFieldProps> = ({
  label,
  ownership,
  onChange,
  placeholder = "Add a team and task..."
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempOwnership, setTempOwnership] = useState<InternalOwnership[]>(ownership || []);
  const [newTeam, setNewTeam] = useState('');
  const [newTask, setNewTask] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    setTempOwnership(ownership || []);
  }, [ownership]);

  const handleAddOwnership = () => {
    if (!newTeam.trim() || !newTask.trim()) return;
    
    const newOwnershipItem: InternalOwnership = {
      id: `ownership-${Date.now()}`,
      team: newTeam.trim(),
      task: newTask.trim()
    };
    
    setTempOwnership([...tempOwnership, newOwnershipItem]);
    setNewTeam('');
    setNewTask('');
  };

  const handleRemoveOwnership = (id: string) => {
    setTempOwnership(tempOwnership.filter(item => item.id !== id));
  };

  const handleSave = () => {
    onChange(tempOwnership);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempOwnership(ownership || []);
    setIsEditing(false);
    setNewTeam('');
    setNewTask('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOwnership();
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <label className="text-[11px] font-medium block text-muted-foreground">
          {label}
        </label>
        
        <div className="space-y-2">
          {/* Ownership list */}
          <div className="flex flex-col gap-1">
            {tempOwnership.map((item, index) => (
              <div 
                key={item.id}
                className="group bg-blue-50 dark:bg-blue-900/20 text-xs px-2 py-1 rounded flex items-center gap-1 border border-blue-200 dark:border-blue-800/30"
              >
                <Users className="h-3 w-3 text-blue-500 dark:text-blue-400 mr-0.5" />
                <span className="text-muted-foreground mr-0.5">{index+1}.</span>
                <span className="font-medium">{item.team}:</span>
                <span>{item.task}</span>
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto text-muted-foreground hover:text-foreground"
                  onClick={() => handleRemoveOwnership(item.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new ownership item */}
          <div className="flex flex-col gap-1">
            <div className="flex-1 relative">
              <Input
                type="text"
                value={newTeam}
                onChange={(e) => setNewTeam(e.target.value)}
                placeholder="Team name..."
                className="text-xs mb-1"
              />
              {newTeam && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-5 w-5 p-0"
                  onClick={() => setNewTeam('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div className="flex gap-1">
              <div className="flex-1 relative">
                <Input
                  type="text"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  placeholder="Task description..."
                  className="text-xs"
                  onKeyDown={handleKeyDown}
                />
                {newTask && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-5 w-5 p-0"
                    onClick={() => setNewTask('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={handleAddOwnership}
                disabled={!newTeam.trim() || !newTask.trim()}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end space-x-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-6 text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium block text-muted-foreground">
        {label}
      </label>
      
      {tempOwnership.length > 0 ? (
        <div 
          className="flex flex-col gap-1 min-h-[2.5rem] cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1 rounded"
          onClick={() => {
            console.log('InternalOwnershipField clicking on non-empty field');
            setIsEditing(true);
          }}
        >
          {tempOwnership.map((item, index) => (
            <div 
              key={item.id}
              className="bg-blue-50 dark:bg-blue-900/20 text-xs px-2 py-1 rounded flex items-center gap-1 border border-blue-200 dark:border-blue-800/30"
            >
              <Users className="h-3 w-3 text-blue-500 dark:text-blue-400 mr-0.5" />
              <span className="text-muted-foreground mr-0.5">{index+1}.</span>
              <span className="font-medium">{item.team}:</span>
              <span>{item.task}</span>
            </div>
          ))}
        </div>
      ) : (
        <div 
          className="text-xs min-h-[2.5rem] cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1 rounded text-muted-foreground italic"
          onClick={() => {
            console.log('InternalOwnershipField clicking on empty field');
            setIsEditing(true);
          }}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default InternalOwnershipField; 